import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { Paper } from "@connected-repo/ui-mui/layout/Paper";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import { useThemeMode } from "@connected-repo/ui-mui/theme/ThemeContext";
import { useEffect } from "react";
import { useRouteError } from "react-router";

export const ErrorFallback = ({ error: propError }: { error?: Error }) => {
    const routeError = useRouteError();
    const error = propError || (routeError as Error);
    const { actualMode } = useThemeMode();
    
    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: actualMode === 'dark' 
                    ? "radial-gradient(circle at top right, #001a35 0%, #020617 100%)"
                    : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                px: 2,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 4, sm: 6 },
                    borderRadius: 4,
                    maxWidth: 500,
                    width: "100%",
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: actualMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 123, 255, 0.2)",
                    boxShadow: actualMode === 'dark' ? "0 24px 48px rgba(0,0,0,0.5)" : "0 12px 40px rgba(0, 123, 255, 0.15)",
                    bgcolor: actualMode === 'dark' ? "rgba(30,30,30,0.8)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Stack spacing={4} alignItems="center">
                    <Box 
                        sx={{ 
                            fontSize: "4rem",
                            filter: "drop-shadow(0 4px 12px rgba(0, 123, 255, 0.3))"
                        }}
                    >
                        ⚡️
                    </Box>
                    <Box>
                        <Typography variant="h4" fontWeight={900} sx={{ 
                            background: "linear-gradient(90deg, #007bff 0%, #00d4ff 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            mb: 1
                        }}>
                            App Error
                        </Typography>
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            Something went wrong within the application layer.
                        </Typography>
                    </Box>

                    {error && (
                        <Box sx={{ 
                            width: "100%", 
                            p: 2, 
                            bgcolor: actualMode === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 123, 255, 0.05)", 
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: actualMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 123, 255, 0.1)",
                            textAlign: "left"
                        }}>
                            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, textTransform: "uppercase" }}>
                                Error Details
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.primary", mt: 0.5, fontFamily: "monospace", wordBreak: "break-all" }}>
                                {error instanceof Error ? error.message : "Internal routing error"}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => window.location.href = "/"}
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            fontWeight: 700,
                            background: "linear-gradient(90deg, #007bff 0%, #00d4ff 100%)",
                            boxShadow: "0 8px 16px rgba(0, 123, 255, 0.2)",
                            "&:hover": {
                                background: "linear-gradient(90deg, #0056b3 0%, #007bff 100%)",
                                transform: "translateY(-1px)"
                            }
                        }}
                    >
                        Return Home
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export const CustomErrorBoundary = () => {
	const error = useRouteError() as Error;

	useEffect(() => {
		if (error) {
            // Lazily capture exception if Sentry is available
            import("@sentry/react").then((Sentry) => {
                Sentry.captureException(error);
            });
		}
	}, [error]);

	return <ErrorFallback error={error} />;
};